export type FileExtension =
  | 'png'
  | 'jpg'
  | 'jpeg'
  | 'gif'
  | 'webp'
  | 'pdf'
  | 'doc'
  | 'docx'
  | 'xls'
  | 'xlsx'
  | 'ppt'
  | 'pptx'
  | 'msg'
  | 'csv'
  | 'ttf'
  | 'otf'
  | 'woff'
  | 'woff2';

// Bytes the file content must contain at the given offsets (all of them must match)
export type Signature = { offset: number; bytes: number[] }[];

export type FileTypeDefinition = {
  // Mime types accepted for the extension
  mimeTypes: string[];
  // The file content must match at least one of them. An empty list skips the content check
  // (plain text formats like CSV have no signature)
  signatures: Signature[];
};

// Browsers send this generic mime type when the OS does not know the extension, which is common
// for fonts and Office documents
const GENERIC_MIME_TYPE = 'application/octet-stream';

const JPEG: FileTypeDefinition = {
  mimeTypes: ['image/jpeg'],
  signatures: [[{ offset: 0, bytes: [0xff, 0xd8, 0xff] }]],
};

// Office 97-2003 documents and Outlook messages (OLE compound file)
const OLE_SIGNATURE: Signature = [
  { offset: 0, bytes: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1] },
];

// Office Open XML documents are ZIP files ("PK\x03\x04")
const ZIP_SIGNATURE: Signature = [{ offset: 0, bytes: [0x50, 0x4b, 0x03, 0x04] }];

// Catalog of the file types that can be validated, by extension.
// If this list keeps growing, replace the mime types with the `mime-types` package.
export const SUPPORTED_FILE_TYPES: Record<FileExtension, FileTypeDefinition> = {
  png: {
    mimeTypes: ['image/png'],
    signatures: [[{ offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] }]],
  },
  jpg: JPEG,
  jpeg: JPEG,
  gif: {
    mimeTypes: ['image/gif'],
    // "GIF8"
    signatures: [[{ offset: 0, bytes: [0x47, 0x49, 0x46, 0x38] }]],
  },
  webp: {
    mimeTypes: ['image/webp'],
    // "RIFF" followed by the file size, then "WEBP"
    signatures: [
      [
        { offset: 0, bytes: [0x52, 0x49, 0x46, 0x46] },
        { offset: 8, bytes: [0x57, 0x45, 0x42, 0x50] },
      ],
    ],
  },
  pdf: {
    mimeTypes: ['application/pdf'],
    // "%PDF"
    signatures: [[{ offset: 0, bytes: [0x25, 0x50, 0x44, 0x46] }]],
  },
  doc: {
    mimeTypes: ['application/msword', GENERIC_MIME_TYPE],
    signatures: [OLE_SIGNATURE],
  },
  docx: {
    mimeTypes: [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      GENERIC_MIME_TYPE,
    ],
    signatures: [ZIP_SIGNATURE],
  },
  xls: {
    mimeTypes: ['application/vnd.ms-excel', GENERIC_MIME_TYPE],
    signatures: [OLE_SIGNATURE],
  },
  xlsx: {
    mimeTypes: [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      GENERIC_MIME_TYPE,
    ],
    signatures: [ZIP_SIGNATURE],
  },
  ppt: {
    mimeTypes: ['application/vnd.ms-powerpoint', GENERIC_MIME_TYPE],
    signatures: [OLE_SIGNATURE],
  },
  pptx: {
    mimeTypes: [
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      GENERIC_MIME_TYPE,
    ],
    signatures: [ZIP_SIGNATURE],
  },
  msg: {
    mimeTypes: ['application/vnd.ms-outlook', GENERIC_MIME_TYPE],
    signatures: [OLE_SIGNATURE],
  },
  csv: {
    // Windows sends application/vnd.ms-excel when Excel is installed
    mimeTypes: ['text/csv', 'text/plain', 'application/csv', 'application/vnd.ms-excel'],
    signatures: [],
  },
  ttf: {
    mimeTypes: ['font/ttf', 'font/sfnt', 'application/x-font-ttf', GENERIC_MIME_TYPE],
    // TrueType (0x00010000) or Apple TrueType ("true")
    signatures: [
      [{ offset: 0, bytes: [0x00, 0x01, 0x00, 0x00] }],
      [{ offset: 0, bytes: [0x74, 0x72, 0x75, 0x65] }],
    ],
  },
  otf: {
    mimeTypes: ['font/otf', 'font/sfnt', 'application/x-font-opentype', GENERIC_MIME_TYPE],
    // "OTTO"
    signatures: [[{ offset: 0, bytes: [0x4f, 0x54, 0x54, 0x4f] }]],
  },
  woff: {
    mimeTypes: ['font/woff', 'application/font-woff', GENERIC_MIME_TYPE],
    // "wOFF"
    signatures: [[{ offset: 0, bytes: [0x77, 0x4f, 0x46, 0x46] }]],
  },
  woff2: {
    mimeTypes: ['font/woff2', GENERIC_MIME_TYPE],
    // "wOF2"
    signatures: [[{ offset: 0, bytes: [0x77, 0x4f, 0x46, 0x32] }]],
  },
};

/** Whether the extension is in the catalog */
export function isSupportedExtension(extension: string): extension is FileExtension {
  return Object.prototype.hasOwnProperty.call(SUPPORTED_FILE_TYPES, extension);
}
